import type { Localized } from '../types/notice'

/**
 * Demo figures for the elected officials' request dashboard.
 * Period figures cover the last 30 days (Sept. 1–30, 2026) and are compared
 * with the previous 30 days. Open requests are counted as of Sept. 30, 2026.
 * The same numbers feed the KPIs, ATNA and the "À votre attention" block.
 */
export interface IssueCount {
  label: Localized
  count: number
  previous: number
}

export interface DashboardStats {
  // Period (last 30 days)
  received: number
  receivedPrev: number
  onTime: number
  onTimePrev: number
  medianDays: number
  medianDaysPrev: number
  satisfaction: number
  satisfactionPrev: number
  surveyResponses: number
  /** Share of closed requests whose citizen answered the survey. */
  surveyRate: number
  issues: IssueCount[]
  // As of today
  open: number
  openAtRisk: number
  late: number
  /** Late requests 30 days ago, for comparison. */
  latePrev: number
}

export const DATA_AS_OF: Localized = {
  fr: 'Données à jour le 30 sept. 2026, 7 h',
  en: 'Data as of Sept. 30, 2026, 7 a.m.',
}

export const TODAY_LABEL: Localized = { fr: '30 sept. 2026', en: 'Sept. 30, 2026' }

/** Whole city — mayor, CAO and directors. */
export const CITY_STATS: DashboardStats = {
  received: 1284,
  receivedPrev: 1157,
  onTime: 94,
  onTimePrev: 91,
  medianDays: 2.6,
  medianDaysPrev: 3.1,
  satisfaction: 4.6,
  satisfactionPrev: 4.4,
  surveyResponses: 312,
  surveyRate: 25,
  issues: [
    { label: { fr: 'Matières résiduelles', en: 'Waste collection' }, count: 320, previous: 236 },
    { label: { fr: 'Nids-de-poule', en: 'Potholes' }, count: 308, previous: 297 },
    { label: { fr: 'Éclairage public', en: 'Street lighting' }, count: 262, previous: 331 },
    { label: { fr: 'Signalisation routière', en: 'Road signage' }, count: 150, previous: 121 },
    { label: { fr: 'Autres', en: 'Other' }, count: 244, previous: 172 },
  ],
  open: 49,
  openAtRisk: 7,
  late: 2,
  latePrev: 4,
}

/** Councillors' districts, keyed by district id. */
export const DISTRICT_STATS: Record<string, DashboardStats> = {
  d1: {
    received: 214,
    receivedPrev: 198,
    onTime: 95,
    onTimePrev: 92,
    medianDays: 2.4,
    medianDaysPrev: 2.9,
    satisfaction: 4.7,
    satisfactionPrev: 4.5,
    surveyResponses: 54,
    surveyRate: 26,
    issues: [
      { label: { fr: 'Matières résiduelles', en: 'Waste collection' }, count: 58, previous: 43 },
      { label: { fr: 'Nids-de-poule', en: 'Potholes' }, count: 56, previous: 54 },
      { label: { fr: 'Éclairage public', en: 'Street lighting' }, count: 44, previous: 58 },
      { label: { fr: 'Signalisation routière', en: 'Road signage' }, count: 19, previous: 18 },
      { label: { fr: 'Autres', en: 'Other' }, count: 37, previous: 25 },
    ],
    open: 8,
    openAtRisk: 0,
    late: 0,
    latePrev: 1,
  },
  d4: {
    received: 97,
    receivedPrev: 102,
    onTime: 96,
    onTimePrev: 95,
    medianDays: 2.3,
    medianDaysPrev: 2.4,
    satisfaction: 4.5,
    satisfactionPrev: 4.6,
    surveyResponses: 23,
    surveyRate: 24,
    issues: [
      { label: { fr: 'Matières résiduelles', en: 'Waste collection' }, count: 27, previous: 20 },
      { label: { fr: 'Nids-de-poule', en: 'Potholes' }, count: 22, previous: 28 },
      { label: { fr: 'Éclairage public', en: 'Street lighting' }, count: 18, previous: 25 },
      { label: { fr: 'Signalisation routière', en: 'Road signage' }, count: 9, previous: 11 },
      { label: { fr: 'Autres', en: 'Other' }, count: 21, previous: 18 },
    ],
    open: 4,
    openAtRisk: 0,
    late: 0,
    latePrev: 0,
  },
}

/** Pothole SLA compliance city-wide, the only service under the 80 % threshold. */
export const POTHOLE_ON_TIME = { current: 71, previous: 82, threshold: 80 }

export interface FlaggedRequest {
  state: 'late' | 'risk'
  service: Localized
  /** Sector letter. */
  sector: string
  /** Districts covering that sector (a sector can straddle two). */
  districtIds: string[]
  days: number
  sla: number
  /** Documented reason, when there is one in the file. */
  reason?: Localized
}

/**
 * Open requests late or close to their deadline (as of today). Matches the
 * map pins: at most 1 late and 3 at-risk requests per district.
 */
export const FLAGGED_REQUESTS: FlaggedRequest[] = [
  {
    state: 'late',
    service: { fr: 'Nid-de-poule', en: 'Pothole' },
    sector: 'B',
    districtIds: ['d2', 'd3'],
    days: 11,
    sla: 7,
    reason: { fr: 'pluies abondantes', en: 'heavy rain' },
  },
  { state: 'risk', service: { fr: 'Bac brisé', en: 'Broken bin' }, sector: 'B', districtIds: ['d2', 'd3'], days: 12, sla: 14 },
  { state: 'risk', service: { fr: 'Bac brisé', en: 'Broken bin' }, sector: 'B', districtIds: ['d2', 'd3'], days: 12, sla: 14 },
  { state: 'late', service: { fr: 'Lampadaire', en: 'Streetlight' }, sector: 'O', districtIds: ['d9'], days: 18, sla: 14 },
  { state: 'risk', service: { fr: 'Bac brisé', en: 'Broken bin' }, sector: 'O', districtIds: ['d9'], days: 12, sla: 14 },
  { state: 'risk', service: { fr: 'Lampadaire', en: 'Streetlight' }, sector: 'I', districtIds: ['d9'], days: 12, sla: 14 },
  { state: 'risk', service: { fr: 'Lampadaire', en: 'Streetlight' }, sector: 'I', districtIds: ['d9'], days: 12, sla: 14 },
  { state: 'risk', service: { fr: 'Bac brisé', en: 'Broken bin' }, sector: 'R', districtIds: ['d7', 'd8'], days: 12, sla: 14 },
  { state: 'risk', service: { fr: 'Bac brisé', en: 'Broken bin' }, sector: 'M', districtIds: ['d11'], days: 12, sla: 14 },
]

/**
 * Districts "à surveiller" (amber on the map): on-time rate under the 80 %
 * threshold. Brossard only.
 */
export const WATCH_DISTRICTS: Record<string, { onTime: number; onTimePrev: number; latePrev: number }> = {
  d3: { onTime: 79, onTimePrev: 86, latePrev: 1 },
  d9: { onTime: 77, onTimePrev: 85, latePrev: 0 },
}

/** Sectors drawn amber in the sector view (where the watch districts' delays sit). */
export const WATCH_SECTOR_IDS = ['br-b', 'br-o', 'br-i']
